import {parse as parseDate, isSameDay, isBefore, startOfToday, format} from 'date-fns'
import {curry, get, sortBy, cloneDeep} from 'lodash/fp'

export const getOr = curry(function(paths, data) {
  return paths.reduce((result, path) => {
    if(result) return result
    return  get(path, data)
  }, undefined)
})

export const renderIf = curry(function(test, component) {
  if(test) return component
  return undefined
})

export const renderIfGet = curry(function(data, test, component) {
  if(get(data, test)) return component
  return undefined
})

export const humanizeDate = function (date) {
  if (date) {
    const d = new Date(
      Number(date.toString().substring(0, 4)),
      Number(date.toString().substring(4, 6)) - 1,
      Number(date.toString().substring(6, 8))
    )
    if (date.toString().substring(4, 6) === '05') { // Are we in May?
      return format(d, 'MMM D, YYYY')
    } else {
      return format(d, 'MMM. D, YYYY')
    }
  } else {
    return ''
  }
}

export const rotateArray = function (arr, order) {
  if (isNaN(order) || order < 0) {
    throw new Error('provide valid order')
  }
  if (arr.length === 0) {
    throw new Error('provide valid array')
  }
  let result = arr.slice(0)
  for (let i = 0; i < order; i++) {
    for (let j = result.length - 1; j > 0; j--) {
      const t = result[j]
      result[j] = result[j - 1]
      result[j - 1] = t
    }
  }
  return result
}

export const familyToLegacyQueryStringCourseCodeMap = {
  'code-101': 'code-101',
  'ops-101': 'ops-101',
  'web3-101': 'web3-101',
  'code-102': 'code-102',
  'ops-102': 'ops-102',
  'code-201': 'code-201',
  'ops-201': 'ops-201',
  'code-301': 'code-301',
  'ops-301': 'ops-301',
  'code-501': 'code-501',
  'code-502': 'code-502',
  'code-java-401': 'code-java-401',
  'code-python-401': 'code-python-401',
  'code-javascript-401': 'code-javascript-401',
  'code-dotnet-401': 'code-dotnet-401',
  'ops-cybersecurity-401': 'ops-cybersecurity-401',
  'ai-aitoolsforcoders-501': 'ai-aitoolsforcoders-501',
  'ai-aitoolsforprofessionals-501': 'ai-aitoolsforprofessionals-501'
}

let base64Encode = (string) => {
  let result = ''
  for(var i=0; i<string.length; i++){
      result += Number(string.charCodeAt(i)).toString(16)
  }
  return result
}

export const prepareRaw = function (schedule, schedType) {
  if (schedType === 'course') {
    let raw = schedule.courses
      .filter(item => !item.cancelled)
      .filter(item => {
        if (!item.course.publishedStatus) return true;
        return item.course.publishedStatus.toLowerCase().trim() === 'planned';
      })
      .filter(item => !!item.course.signupUrl)
      .map(item => {
        let course = item.course;
        course.queryStringFilterName = familyToLegacyQueryStringCourseCodeMap[course.sequenceAndFamily];
        course.quarter = (course.quarter.startsWith('q') ? '' : 'q') + course.quarter;
        course.startDate = parseDate(course.startDate);
        course.endDate = parseDate(course.endDate);
        course.endDate = isSameDay(course.startDate, course.endDate) ? undefined : course.endDate;
        course.applicationDeadline = course.applicationDeadline ? parseDate(course.applicationDeadline) : null;
        course.scholarshipDeadline = course.scholarshipDeadline ? parseDate(course.scholarshipDeadline) : null;
        if (course.code == course.title) {
          course.urlHash = base64Encode(course.code);
        } else {
          course.urlHash = course.code;
        }
        return course;
      });
    return raw;
  } else if (schedType === 'event') {
      return processEventListings(schedule);
  } else {
     return [];
  }
}

export const prepareSchedule = function (schedule, schedType) {
  schedule = cloneDeep(schedule);
  let raw;
  if (schedType === 'course') {
    raw = prepareRaw(schedule, schedType);
  } else if (schedType === 'event') {
    raw = prepareRaw(schedule.events, schedType);
  } else {
    raw = [];
  }
  raw = raw
    .filter(item => isBefore(startOfToday(), item.startDate))
    .filter(item => item.signupUrl !== 'https://www.eventbrite.com/e/seattle-code-101-software-development-careers-dec-8-2018-tickets-39750062507');

  return sortBy(item => item.startDate, sortBy(item => item.family, raw));
}

export const processEventListings = (jsonData) => {
  return (jsonData.events).map(events => {
    const eventData = events.event;
    const eventImage = eventData.image;
    const event_type = eventData.type;
    const title = eventData.title;
    const start_date = eventData.startDate;
    const end_date = eventData.endDate;
    const formatted_date = eventData.formattedEventDate;
    const start_time = eventData.startTime;
    const end_time = eventData.endTime;
    const description = eventData.description;
    const location = eventData.location.name;
    const address = eventData.location.streetAddress;
    const city = eventData.location.city;
    const state = eventData.location.state;
    const zip_code = eventData.location.zipCode;
    const signup_url = eventData.signupUrl;

    return {
      eventImage,
      event_type,
      title,
      start_date,
      end_date,
      formatted_date,
      start_time,
      end_time,
      description,
      location,
      address,
      city,
      state,
      zip_code,
      signup_url,
    };
  })
  .filterEvents();
}

Array.prototype.filterEvents = function () {
  return this
    .map(event => ({
      ...event,
      start_date: event.start_date ? parseDate(event.start_date) : null,
    }))
    // Filter out any events that are older than the current date/time
    .filter(event => {
      const currentDateFormat = new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
      const currentDate = new Date(currentDateFormat);
      const eventStartDate = new Date(event.start_date);
      const eventStartTime = event.start_time;
      const eventStartDateTime = new Date(`${eventStartDate.toDateString()} ${eventStartTime}`);
      if (!event.start_date) { return true; }
      if (eventStartDateTime < currentDate) { return false; }
      return true;
    })
    // Sort upcoming events in ascending order - events without dates will appear at the top.
    .sort((a, b) => {
      if (!a.start_date && !b.start_date) {
        return 0;
      } else if (!a.start_date) {
        return -1;
      } else if (!b.start_date) {
        return 1;
      } else {
        const aStartDateTime = new Date(`${a.start_date.toDateString()} ${a.start_time}`);
        const bStartDateTime = new Date(`${b.start_date.toDateString()} ${b.start_time}`);
        return aStartDateTime - bStartDateTime;
      }
    })
}


// WEBPACK FOOTER //
// ./src/lib/util.js